import { Box, Skeleton, SxProps, Typography, alpha } from '@mui/material';
import dayjs from 'dayjs';
import capitalize from 'lodash.capitalize';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { DeviceV3 } from '../../../../backendsdk';
import useApi from '../../../../hooks/api';
import { useImages } from '../../../../hooks/images';
import useIsMobile from '../../../../hooks/isMobile';
import useProfile from '../../../../hooks/profile';
import imageNA from '../../../../images/image_not_available.png';
import palette from '../../../ColorPalette';
import { WELLNESS_TYPE_MAP } from '../../Device/WellnessBox';
import { wellnessColorsByState } from '../../StyledElements';
import { getFFWellnessDetails, getICWellnessDetails } from '../utils';

export const ACTION_ITEMS = [
    'content.wellness.adjust_clean',
    'content.wellness.adjust',
    'content.wellness.remove_blockage_clean',
    'content.wellness.connect_camera',
] as const;
type ActionItem = typeof ACTION_ITEMS[number];
type WellnessType = keyof typeof WELLNESS_TYPE_MAP;
export const WELLNESS_TO_ACTION_MAP: Partial<Record<WellnessType, ActionItem>> = {
    frontfacingwellnessunwellhorizontal: 'content.wellness.adjust_clean',
    frontfacingwellnesscameraverticalhigh: 'content.wellness.adjust_clean',
    frontfacingwellnesscameraverticallow: 'content.wellness.adjust_clean',
    frontfacingwellnesslowqualityinput: 'content.wellness.adjust_clean',
    frontfacingwellnessinsufficientvalidinput: 'content.wellness.adjust_clean',
    faulty: 'content.wellness.adjust',
    'no face': 'content.wellness.adjust',
    no_face: 'content.wellness.adjust',
    peripheral_face: 'content.wellness.adjust',
    blocked: 'content.wellness.remove_blockage_clean',
    disconnected: 'content.wellness.connect_camera',
};

interface WellnessPanelProps {
    device: DeviceV3;
}

const WellnessPanel: React.FC<WellnessPanelProps> = ({ device }: WellnessPanelProps) => {
    const { profile } = useProfile();
    const isMobile = useIsMobile();

    let icWellnessType = 'in_cabin';
    if (
        device.aggregated_wellness &&
        device.aggregated_wellness.in_cabin_wellness &&
        'wellness_state' in device.aggregated_wellness.in_cabin_wellness
    ) {
        icWellnessType = 'in_cabin_v1';
    }

    const icWellnessId = device.aggregated_wellness?.in_cabin_wellness?.id;
    const ffWellnessId = device.aggregated_wellness?.front_facing_wellness?.id;
    const icWellnessDetails = getICWellnessDetails(device, profile.customer.settings.inactive_device_threshold);
    const ffWellnessDetails = getFFWellnessDetails(device, profile.customer.settings.inactive_device_threshold);
    const icWellnessTimestamp = device.aggregated_wellness?.in_cabin_wellness?.timestamp;
    const ffWellnessTimestamp = device.aggregated_wellness?.front_facing_wellness?.timestamp;
    const icActionValue = WELLNESS_TO_ACTION_MAP[icWellnessDetails];
    const ffActionValue = WELLNESS_TO_ACTION_MAP[ffWellnessDetails as keyof typeof WELLNESS_TO_ACTION_MAP];

    return (
        <Box
            sx={{
                width: '100%',
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
            }}
        >
            {icWellnessId ? (
                <WellnessImage
                    wellnessType={icWellnessType}
                    wellnessId={icWellnessId}
                    wellnessStatus={icWellnessDetails}
                    wellnessTimestamp={icWellnessTimestamp}
                    actionValue={icActionValue}
                    sx={{ mr: isMobile ? 0 : 1, mb: isMobile ? 1 : 0 }}
                />
            ) : null}
            {ffWellnessId ? (
                <WellnessImage
                    wellnessType="front_facing"
                    wellnessId={ffWellnessId}
                    wellnessStatus={ffWellnessDetails}
                    wellnessTimestamp={ffWellnessTimestamp}
                    actionValue={ffActionValue}
                />
            ) : null}
        </Box>
    );
};

interface WellnessImageProps {
    wellnessType: string;
    wellnessId: number;
    wellnessStatus?: string;
    wellnessTimestamp?: number;
    actionValue?: string;
    sx?: SxProps;
}

export const WellnessImage: React.FC<WellnessImageProps> = ({
    wellnessType,
    wellnessId,
    wellnessStatus,
    wellnessTimestamp,
    actionValue,
    sx,
}: WellnessImageProps) => {
    const { api } = useApi();
    const getImage = (imageKey: string) => {
        const [wellnessType, wellnessId] = imageKey.split(':', 2);
        return api.apiV2WellnessImageWellnessTypeGet(
            {
                wellnessType,
                wellnessId,
            },
            { responseType: 'blob' },
        );
    };
    const [wellnessImages, addToQueue, imageSizes] = useImages(2, getImage, true);
    const { t } = useTranslation();
    const { profile } = useProfile();

    const wellnessKey = `${wellnessType}:${wellnessId}`;

    useEffect(() => {
        addToQueue([wellnessKey]);
    }, [wellnessKey]);

    const wellnessImageSkeleton = (
        <div style={{ width: '100%', aspectRatio: 4 / 3 }}>
            <Skeleton variant="rectangular" width="100%" height="100%" animation="wave" />
        </div>
    );

    const imageSize = imageSizes[wellnessKey];

    const shouldCrop =
        imageSize?.width !== undefined &&
        imageSize?.height !== undefined &&
        imageSize.width / imageSize.height === 8 / 3;

    const wellnessTypeKey = wellnessType.includes('in_cabin') ? 'in_cabin' : 'front_facing';
    const wellnessKeyShort = wellnessTypeKey === 'in_cabin' ? 'ic' : 'ff';

    return (
        <Box
            sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'start',
                ...sx,
            }}
        >
            {!!wellnessStatus ? (
                <>
                    <Typography sx={{ fontSize: 12, color: 'grayColor.main', fontWeight: 'bold' }}>
                        {t(`content.wellness.${wellnessType}`)}
                    </Typography>
                    <Typography
                        data-testid={`${wellnessTypeKey.replaceAll('_', '-')}-wellness`}
                        sx={{
                            fontSize: 16,
                            fontWeight: 'bold',
                            color: wellnessColorsByState[wellnessStatus as keyof typeof wellnessColorsByState],
                        }}
                    >
                        {capitalize(t(WELLNESS_TYPE_MAP[wellnessStatus as keyof typeof WELLNESS_TYPE_MAP]))}
                    </Typography>
                </>
            ) : null}
            {wellnessImages[wellnessKey] ? (
                <div style={{ width: '100%', aspectRatio: 4 / 3, position: 'relative' }}>
                    <img
                        src={wellnessImages[wellnessKey]}
                        className={`wellness-image ${shouldCrop ? `${wellnessKeyShort}-image` : ''}`}
                    />
                    {!!wellnessTimestamp && wellnessImages[wellnessKey] !== imageNA ? (
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                backgroundColor: alpha(palette.black, 0.3),
                                px: 0.5,
                            }}
                        >
                            <Typography sx={{ fontSize: 12, fontWeight: 'bold', color: palette.white }}>
                                {dayjs.unix(wellnessTimestamp).format(profile.dateTimeFormat)}
                            </Typography>
                        </Box>
                    ) : null}
                </div>
            ) : (
                wellnessImageSkeleton
            )}
            {actionValue !== undefined ? (
                <Typography
                    data-testid={`${wellnessType}-action`}
                    sx={{
                        mt: 0.25,
                        fontSize: 14,
                        fontWeight: 500,
                        color: palette.primary,
                    }}
                >
                    {t(actionValue)}
                </Typography>
            ) : null}
        </Box>
    );
};

export default WellnessPanel;
